import { Fragment, useRef } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';

import { FilterDropdownTrigger } from './FilterDropdownTrigger';

export interface FilterDropdownOption<T extends string> {
  value: T;
  label: string;
  Icon: LBIcon;
}

interface FilterDropdownMenuProps<T extends string> {
  'aria-label': string;
  allLabel: string;
  optionGroups: readonly (readonly FilterDropdownOption<T>[])[];
  selectedValues: readonly T[];
  onToggle: (value: T) => void;
  onClearAll: () => void;
  contentClassName?: string;
  compact?: boolean;
}

export function FilterDropdownMenu<T extends string>({
  'aria-label': ariaLabel,
  allLabel,
  optionGroups,
  selectedValues,
  onToggle,
  onClearAll,
  contentClassName,
  compact = false,
}: FilterDropdownMenuProps<T>) {
  const closedByPointerRef = useRef(false);
  const activeLabel =
    selectedValues.length > 1
      ? `${selectedValues.length} filters`
      : (optionGroups
          .flat()
          .find((option) => option.value === selectedValues[0])?.label ?? null);

  return (
    <DropdownMenu.Root>
      <FilterDropdownTrigger
        aria-label={ariaLabel}
        activeLabel={activeLabel}
        compact={compact}
      />
      <DropdownMenu.Content
        align="end"
        side="bottom"
        fit
        className={contentClassName}
        onPointerDownOutside={() => {
          closedByPointerRef.current = true;
        }}
        onCloseAutoFocus={(event) => {
          if (closedByPointerRef.current) event.preventDefault();
          closedByPointerRef.current = false;
        }}
      >
        <FilterDropdownItem
          Icon={Icons.listFilter}
          label={allLabel}
          isSelected={selectedValues.length === 0}
          onSelect={onClearAll}
        />
        {optionGroups.map((options, groupIndex) => (
          <Fragment key={groupIndex}>
            {groupIndex > 0 && <DropdownMenu.Separator />}
            {options.map(({ value, label, Icon }) => (
              <FilterDropdownItem
                key={value}
                Icon={Icon}
                label={label}
                isSelected={selectedValues.includes(value)}
                onSelect={() => onToggle(value)}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

const FilterDropdownItem = ({
  Icon,
  label,
  isSelected,
  onSelect,
}: {
  Icon: LBIcon;
  label: string;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <DropdownMenu.Item
    role="menuitemcheckbox"
    aria-checked={isSelected}
    onSelect={(event) => {
      event.preventDefault();
      onSelect();
    }}
  >
    <Icon className="size-3.5 shrink-0 opacity-75" />
    <span className="flex-1 truncate">{label}</span>
    {isSelected && (
      <Icons.check className="ml-auto size-3.5 shrink-0 opacity-75" />
    )}
  </DropdownMenu.Item>
);
